import styled from 'styled-components';
import { useRef, useState } from 'react';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { DateRangePicker } from 'ui/datePicker';
import { RELATIVE_DATE_PRESETS } from 'ui/datePicker/helpers';
import { ChevronDown } from 'ui/icons';
import { useClickAway } from 'react-use';
import noop from 'utils/noop';
import { useBillingQueryContext } from '../../contexts';
import { AbsolutePositionWrapper, LightText } from './BillingPagePageActions.styled';
import { yesterday, sevenDaysAgo, fourteenDaysAgo, thirtyDaysAgo } from '../../utils';

export const DATE_PRESETS = {
  /**
   * Presets only include up until yesterday, because metrics are only available
   * up until yesterday.
   */
  YESTERDAY: {
    menuLabel: 'Yesterday',
    format: () => ({
      startDate: yesterday(),
      endDate: yesterday(),
    }),
  },
  PAST_7_DAYS: {
    menuLabel: 'Past 7 days',
    format: () => ({
      startDate: sevenDaysAgo(),
      endDate: yesterday(),
    }),
  },
  PAST_14_DAYS: {
    menuLabel: 'Past 14 days',
    format: () => ({
      startDate: fourteenDaysAgo(),
      endDate: yesterday(),
    }),
  },
  PAST_30_DAYS: {
    menuLabel: 'Past 30 days',
    format: () => ({
      startDate: thirtyDaysAgo(),
      endDate: yesterday(),
    }),
  },
  CUSTOM_RANGE: RELATIVE_DATE_PRESETS.CUSTOM_RANGE,
};

const RelativePositionWrapper = styled.div`
  position: relative;
`;

export const BillingDateRangePicker = () => {
  const { dateRange, displayDateRange, setDateRange } = useBillingQueryContext();
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);

  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, () => {
    setIsDateRangePickerOpen(false);
  });

  return (
    <RelativePositionWrapper>
      <SecondaryButton
        data-test="date-range-picker-trigger"
        onClick={() => setIsDateRangePickerOpen(!isDateRangePickerOpen)}
        size={BUTTON_SIZES.LARGE}
      >
        <span>{displayDateRange}</span>
        <LightText>(PST)</LightText>
        <ChevronDown width={16} />
      </SecondaryButton>
      <AbsolutePositionWrapper ref={wrapperRef}>
        {isDateRangePickerOpen && (
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onCancel={() => setIsDateRangePickerOpen(false)}
            onDateRangeChange={setDateRange}
            onTimeZoneChange={noop}
            validDateRange={{ end: yesterday() }}
            presets={DATE_PRESETS}
            promoteCustomInput
            showRelativeDatePresets
            hideTimeInputs
            hideTimeZonePicker
            useLocalTimeZone
          />
        )}
      </AbsolutePositionWrapper>
    </RelativePositionWrapper>
  );
};
